import { useCallback, useRef, useState } from "react";

/** Zustand eines laufenden oder beendeten Durchlaufs. */
export interface SequentiellerLaufZustand {
  /** True, solange der Durchlauf läuft */
  laeuft: boolean;
  /** Nummer des gerade bearbeiteten Elements, 1-basiert (0 = noch nicht gestartet) */
  aktuell: number;
  /** Gesamtzahl der Elemente dieses Durchlaufs */
  gesamt: number;
  /** Bezeichnung des gerade bearbeiteten Elements */
  label: string;
}

const LEERER_ZUSTAND: SequentiellerLaufZustand = {
  laeuft: false,
  aktuell: 0,
  gesamt: 0,
  label: "",
};

/**
 * Ergebnis eines Durchlaufs: je Element entweder ein Wert oder ein Fehler.
 * Ein gescheitertes Element bricht den Durchlauf nicht ab.
 */
export interface LaufErgebnis<TElement, TWert> {
  erfolge: { element: TElement; wert: TWert }[];
  fehler: { element: TElement; fehler: unknown }[];
  /** True, wenn der Nutzer abgebrochen hat – dann sind nicht alle Elemente bearbeitet */
  abgebrochen: boolean;
}

/**
 * Arbeitet eine Liste nacheinander ab und hält Fortschritt, Abbruch und Fehler fest.
 *
 * Bewusst sequenziell: Die aufgerufene Operation kann teuer sein, und der Fortschritt soll
 * ehrlich anzeigen, welches Element gerade dran ist. Wer nur die Anzeige braucht, nimmt
 * `ProgressDialog` allein – der Hook kennt keine Oberfläche.
 *
 * @param labelVon Liefert die Bezeichnung eines Elements für die Fortschrittsanzeige
 */
export function useSequentiellerLauf<TElement, TWert>(
  labelVon: (element: TElement) => string,
) {
  const [zustand, setZustand] =
    useState<SequentiellerLaufZustand>(LEERER_ZUSTAND);

  // Ref statt State: Die laufende Schleife liest den Wert bei jedem Schritt neu. Über State
  // würde sie die Closure vom Start des Durchlaufs sehen und den Abbruch nie bemerken.
  const abbruchRef = useRef(false);

  const abbrechen = useCallback(() => {
    abbruchRef.current = true;
  }, []);

  /**
   * Startet den Durchlauf. Löst nicht aus, wenn bereits einer läuft.
   *
   * @param elemente Die abzuarbeitenden Elemente
   * @param arbeit Wird je Element aufgerufen; ein Fehler darin überspringt nur dieses Element
   */
  const starten = useCallback(
    async (
      elemente: TElement[],
      arbeit: (element: TElement) => Promise<TWert>,
    ): Promise<LaufErgebnis<TElement, TWert>> => {
      abbruchRef.current = false;

      const erfolge: { element: TElement; wert: TWert }[] = [];
      const fehler: { element: TElement; fehler: unknown }[] = [];

      setZustand({
        laeuft: true,
        aktuell: 0,
        gesamt: elemente.length,
        label: "",
      });

      for (let i = 0; i < elemente.length; i++) {
        if (abbruchRef.current) break;

        const element = elemente[i];
        setZustand({
          laeuft: true,
          aktuell: i + 1,
          gesamt: elemente.length,
          label: labelVon(element),
        });

        try {
          erfolge.push({ element, wert: await arbeit(element) });
        } catch (e) {
          fehler.push({ element, fehler: e });
        }
      }

      setZustand(LEERER_ZUSTAND);

      return { erfolge, fehler, abgebrochen: abbruchRef.current };
    },
    [labelVon],
  );

  return { zustand, starten, abbrechen };
}
